import { useState, useMemo } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useAdminI18n } from "@/hooks/useAdminI18n";
import {
  useUsersDB,
  profileRoleLabels,
  profileRoleColors,
  rolePermissions,
  ALL_PROFILE_ROLES,
  type UserProfile,
  type ProfileRole,
  type UsersDBError,
} from "@/hooks/useUsersDB";

/* ─── Constants ─────────────────────────────────────────────────────────────── */

const ALL_PERMISSIONS = [
  { key: "dashboard",   label: "Dashboard" },
  { key: "articles",    label: "Artikel" },
  { key: "categories",  label: "Kategorien" },
  { key: "authors",     label: "Autoren" },

  { key: "media",       label: "Mediathek" },
  { key: "ads",         label: "Werbung" },
  { key: "homepage",    label: "Startseite" },
  { key: "breaking",    label: "Breaking News" },
  { key: "seo",         label: "SEO" },
  { key: "newsletter",  label: "Newsletter" },
  { key: "pages",       label: "Seiten" },
  { key: "settings",    label: "Einstellungen" },
  { key: "users",       label: "Benutzer & Rollen" },
  { key: "analytics",   label: "Analytik" },
  { key: "brand",       label: "Marke" },
];

/* ─── Helpers ────────────────────────────────────────────────────────────────── */

function getInitials(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/* ─── Inline Assign-Role Form ────────────────────────────────────────────────── */
// Shown directly on the page (not in a modal) so it's always visible.
// Saves to public.profiles by email. No fake users created.

interface AssignFormProps {
  onAssign: (email: string, role: ProfileRole, name: string) => Promise<UsersDBError | null>;
}

function AssignRoleForm({ onAssign }: AssignFormProps) {
  const { t } = useAdminI18n();
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [role, setRole]   = useState<ProfileRole>("author");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState<UsersDBError | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSuccess(false);
    setLoading(true);
    const error = await onAssign(email.trim().toLowerCase(), role, name.trim());
    setLoading(false);
    if (error) {
      setErr(error);
    } else {
      setSuccess(true);
      setEmail("");
      setName("");
      setRole("author");
      setTimeout(() => setSuccess(false), 4000);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-hj-gray-border p-5 mb-6">
      <h2 className="text-sm font-bold text-hj-dark mb-1">{t("users.assignForm.title")}</h2>
      <p className="text-xs text-hj-gray mb-4">
        {t("users.assignForm.subtitle")}
      </p>

      {err && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm font-medium text-red-700">{err.message}</p>
          {err.detail && <p className="text-xs text-red-500 mt-1 leading-relaxed">{err.detail}</p>}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-2">
          <i className="ri-check-line text-emerald-600"></i>
          <p className="text-sm font-medium text-emerald-700">{t("users.assignForm.success")}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-hj-dark mb-1.5">
            {t("users.assignForm.email")} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={t("users.assignForm.emailPlaceholder")}
            className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-hj-dark mb-1.5">{t("users.assignForm.role")} <span className="text-red-500">*</span></label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as ProfileRole)}
            className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
          >
            {ALL_PROFILE_ROLES.map((r) => (
              <option key={r} value={r}>{profileRoleLabels[r]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-hj-dark mb-1.5">{t("users.assignForm.name")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("users.assignForm.namePlaceholder")}
            className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
          />
        </div>
        <div className="sm:col-span-3 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>{t("users.assignForm.saving")}</>
            ) : (
              <><i className="ri-shield-user-line"></i>{t("users.assignForm.submit")}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─── Edit Role Modal ────────────────────────────────────────────────────────── */

interface EditRoleModalProps {
  user: UserProfile;
  onClose: () => void;
  onSave: (userId: string, role: ProfileRole) => Promise<UsersDBError | null>;
}

function EditRoleModal({ user, onClose, onSave }: EditRoleModalProps) {
  const { t } = useAdminI18n();
  const [role, setRole] = useState<ProfileRole>(user.role);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<UsersDBError | null>(null);

  const handleSave = async () => {
    setErr(null);
    setLoading(true);
    const error = await onSave(user.id, role);
    setLoading(false);
    if (error) {
      setErr(error);
    } else {
      onClose();
    }
  };

  const perms = rolePermissions[role] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-hj-dark">{t("users.modal.editTitle")}</h3>
            <p className="text-sm text-hj-gray mt-0.5">{user.name || "—"} · {user.email}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer">
            <i className="ri-close-line text-hj-gray"></i>
          </button>
        </div>

        {err && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-700">{err.message}</p>
            {err.detail && <p className="text-xs text-red-500 mt-1">{err.detail}</p>}
          </div>
        )}

        {/* Role selector */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-hj-dark mb-2">{t("users.modal.newRole")}</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {ALL_PROFILE_ROLES.map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`px-3 py-2.5 rounded-md text-sm font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  role === r
                    ? "border-hj-red bg-hj-red/5 text-hj-red"
                    : "border-hj-gray-border text-hj-gray hover:border-hj-red/40"
                }`}
              >
                {profileRoleLabels[r]}
              </button>
            ))}
          </div>
        </div>

        {/* Permissions preview */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-hj-gray uppercase tracking-wider mb-2">
            {t("users.modal.permissionsFor")} {profileRoleLabels[role]}
          </p>
          <div className="grid grid-cols-2 gap-1.5">
            {ALL_PERMISSIONS.map((perm) => {
              const has = perms.includes(perm.key);
              return (
                <div key={perm.key} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs ${has ? "bg-emerald-50 text-emerald-700" : "bg-gray-50 text-gray-400"}`}>
                  <i className={has ? "ri-check-line" : "ri-close-line"}></i>
                  {perm.label}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-hj-gray-border text-hj-gray text-sm font-medium rounded-md hover:bg-hj-gray-light transition-colors cursor-pointer whitespace-nowrap">
            {t("users.modal.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={loading || role === user.role}
            className="flex-1 py-2.5 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            {loading ? t("users.modal.saving") : t("users.modal.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────────── */

export default function AdminUsers() {
  const { session } = useAdminAuth();
  const { t } = useAdminI18n();
  const {
    users, loading, fetchError, fetchUsers,
    updateUserRole, toggleUserActive, assignRoleByEmail,
  } = useUsersDB();

  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState<ProfileRole | "all">("all");
  const [filterStatus, setFilterStatus] = useState<"all" | "active" | "inactive">("all");
  const [editUser, setEditUser] = useState<UserProfile | null>(null);
  const [toast, setToast] = useState<{ msg: string; detail?: string; type: "success" | "error" } | null>(null);

  const isSuperAdmin = session?.role === "super_admin" || session?.role === "admin";

  const showToast = (msg: string, type: "success" | "error" = "success", detail?: string) => {
    setToast({ msg, type, detail });
    setTimeout(() => setToast(null), 5000);
  };

  const handleRoleSave = async (userId: string, role: ProfileRole): Promise<UsersDBError | null> => {
    const err = await updateUserRole(userId, role);
    if (!err) showToast("Rolle erfolgreich aktualisiert.");
    return err;
  };

  const handleToggleActive = async (user: UserProfile) => {
    const err = await toggleUserActive(user.id, !user.is_active);
    if (err) showToast(err.message, "error", err.detail);
    else showToast(`Benutzer ${user.is_active ? "deaktiviert" : "aktiviert"}.`);
  };

  const handleAssignRole = async (
    email: string, role: ProfileRole, name: string
  ): Promise<UsersDBError | null> => {
    const err = await assignRoleByEmail(email, role, name);
    if (!err) showToast("Rolle erfolgreich gespeichert.");
    return err;
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      const matchSearch =
        (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole = filterRole === "all" || u.role === filterRole;
      const matchStatus =
        filterStatus === "all" ||
        (filterStatus === "active" && u.is_active) ||
        (filterStatus === "inactive" && !u.is_active);
      return matchSearch && matchRole && matchStatus;
    });
  }, [users, search, filterRole, filterStatus]);

  const roleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    users.forEach((u) => { counts[u.role] = (counts[u.role] || 0) + 1; });
    return counts;
  }, [users]);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("users.title")}</h1>
            <p className="text-sm text-hj-gray mt-0.5">
              {loading ? t("common.loading") : `${users.length} ${t("users.header.subtitle")}`}
            </p>
          </div>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="px-3 py-2 border border-hj-gray-border text-hj-gray text-sm rounded-md hover:bg-hj-gray-light transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            {t("users.refresh")}
          </button>
        </div>

        {/* ── Assign Role Form (always visible for super_admin / admin) ── */}
        {isSuperAdmin && (
          <AssignRoleForm onAssign={handleAssignRole} />
        )}

        {/* How-to notice */}
        <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex gap-3">
            <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-information-line text-slate-500"></i>
            </div>
            <div className="text-sm text-slate-700 space-y-1">
              <p className="font-semibold text-slate-800">{t("users.howTo.title")}</p>
              <ol className="list-decimal list-inside space-y-1 text-slate-600">
                <li>{t("users.howTo.step1")}</li>
                <li>{t("users.howTo.step2")}</li>
                <li>{t("users.howTo.step3")}</li>
              </ol>
              <p className="text-xs text-slate-500 pt-1">
                {t("users.howTo.note")}
              </p>
            </div>
          </div>
        </div>

        {/* Fetch error */}
        {fetchError && (
          <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-error-warning-line text-red-600 mt-0.5 shrink-0"></i>
              <div>
                <p className="text-sm font-medium text-red-700">{fetchError.message}</p>
                {fetchError.detail && <p className="text-xs text-red-500 mt-1 font-mono">{fetchError.detail}</p>}
                <button onClick={fetchUsers} className="mt-2 text-xs text-red-600 underline cursor-pointer">
                  {t("common.refresh")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Role summary cards */}
        {!fetchError && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
            {ALL_PROFILE_ROLES.map((role) => (
              <button
                key={role}
                onClick={() => setFilterRole(filterRole === role ? "all" : role)}
                className={`rounded-lg border p-3 text-left transition-all cursor-pointer ${
                  filterRole === role
                    ? "border-hj-red bg-hj-red/5"
                    : "border-hj-gray-border bg-white hover:border-hj-red/40"
                }`}
              >
                <p className="text-xl font-bold text-hj-dark">{roleCounts[role] ?? 0}</p>
                <p className="text-xs text-hj-gray mt-0.5 truncate">{profileRoleLabels[role]}</p>
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        {!fetchError && (
          <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name oder E-Mail suchen..."
                className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
              />
            </div>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as ProfileRole | "all")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
            >
              <option value="all">{t("users.filter.allRoles")}</option>
              {ALL_PROFILE_ROLES.map((r) => (
                <option key={r} value={r}>{profileRoleLabels[r]}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as "all" | "active" | "inactive")}
              className="px-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red bg-white cursor-pointer"
            >
              <option value="all">{t("users.filter.allStatus")}</option>
              <option value="active">{t("users.filter.active")}</option>
              <option value="inactive">{t("users.filter.inactive")}</option>
            </select>
          </div>
        )}

        {/* Users table */}
        {!fetchError && (
          <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin w-7 h-7 border-2 border-hj-red border-t-transparent rounded-full"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-hj-gray">
                <i className="ri-user-search-line text-4xl mb-3 opacity-40"></i>
                {users.length === 0 ? (
                  <>
                    <p className="text-sm font-medium text-hj-dark mb-1">
                      {t("users.table.noProfiles")}
                    </p>
                    <p className="text-xs text-hj-gray mt-1 max-w-md text-center leading-relaxed">
                      {t("users.table.noProfilesHint")}
                    </p>
                  </>
                ) : (
                  <p className="text-sm">{t("users.table.empty")}</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-hj-gray-light">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">Benutzer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">Rolle</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden md:table-cell">Erstellt</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden lg:table-cell">Letzter Login</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-hj-gray uppercase tracking-wider">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-hj-gray-border">
                    {filtered.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-hj-red/10 flex items-center justify-center shrink-0 text-xs font-bold text-hj-red">
                              {getInitials(user.name || user.email)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-hj-dark">{user.name || "—"}</p>
                              <p className="text-xs text-hj-gray">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${profileRoleColors[user.role] ?? "bg-gray-100 text-gray-500"}`}>
                            {profileRoleLabels[user.role] ?? user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-hj-gray hidden md:table-cell">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-hj-gray hidden lg:table-cell">
                          {formatDate(user.last_sign_in_at)}
                        </td>
                        <td className="px-4 py-3">
                          {isSuperAdmin ? (
                            <button
                              onClick={() => handleToggleActive(user)}
                              className={`px-2 py-0.5 text-xs font-medium rounded-full transition-colors cursor-pointer ${
                                user.is_active
                                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                              title={user.is_active ? "Klicken zum Deaktivieren" : "Klicken zum Aktivieren"}
                            >
                              {user.is_active ? "Aktiv" : "Inaktiv"}
                            </button>
                          ) : (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${user.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                              {user.is_active ? t("users.status.active") : t("users.status.inactive")}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isSuperAdmin ? (
                            <button
                              onClick={() => setEditUser(user)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-hj-gray-border rounded-md hover:border-hj-red hover:text-hj-red transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-shield-user-line"></i>
                              {t("users.changeRole")}
                            </button>
                          ) : (
                            <span className="text-xs text-hj-gray">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Permissions reference table */}
        <div className="mt-6 bg-white rounded-lg border border-hj-gray-border p-5">
          <h2 className="text-sm font-bold text-hj-dark mb-4">{t("users.permissions.title")}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-hj-gray-border">
                  <th className="pb-2 text-left font-semibold text-hj-gray pr-4">{t("users.permissions.permission")}</th>
                  {ALL_PROFILE_ROLES.map((r) => (
                    <th key={r} className="pb-2 text-center font-semibold text-hj-gray px-2 whitespace-nowrap">
                      {profileRoleLabels[r]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-hj-gray-border/50">
                {ALL_PERMISSIONS.map((perm) => (
                  <tr key={perm.key} className="hover:bg-gray-50">
                    <td className="py-2 pr-4 text-hj-dark font-medium">{perm.label}</td>
                    {ALL_PROFILE_ROLES.map((r) => {
                      const has = (rolePermissions[r] || []).includes(perm.key);
                      return (
                        <td key={r} className="py-2 text-center px-2">
                          {has
                            ? <i className="ri-check-line text-emerald-500"></i>
                            : <i className="ri-close-line text-gray-300"></i>
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Role Modal */}
      {editUser && (
        <EditRoleModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSave={handleRoleSave}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm font-medium flex items-start gap-2 max-w-sm ${toast.type === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"}`}>
          <i className={`mt-0.5 shrink-0 ${toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"}`}></i>
          <div>
            <p>{toast.msg}</p>
            {toast.detail && <p className="text-xs opacity-80 mt-0.5">{toast.detail}</p>}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
